<template>
  <div class="sz home">
    <SalonNavigation />

    <!-- ==================== HERO ==================== -->
    <section class="hero">
      <div class="sz-wrap hero__grid">
        <div class="hero__copy">
          <span class="sz-eyebrow">For barbershops, salons &amp; studios</span>
          <h1 class="hero__title">
            Every chair booked.<br />
            <em class="sz-accent">Every client remembered.</em>
          </h1>
          <p class="sz-lede hero__lede">
            Salon Zap runs the whole shop from one place: online booking, reminders, checkout,
            client profiles, team payouts, and the numbers that tell you how it's going. On the web,
            iPhone, and Android.
          </p>
          <div class="hero__ctas">
            <router-link to="/demo" class="sz-btn sz-btn--primary">
              Book a free demo
              <v-icon size="18" color="#FFFFFF">mdi-arrow-right</v-icon>
            </router-link>
            <router-link to="/features" class="sz-btn sz-btn--ghost">See every feature</router-link>
          </div>
          <div class="hero__trust">
            <span><v-icon size="16" color="#3F6F55">mdi-check-circle</v-icon> $120/month flat</span>
            <span><v-icon size="16" color="#3F6F55">mdi-check-circle</v-icon> No setup fees</span>
            <span><v-icon size="16" color="#3F6F55">mdi-check-circle</v-icon> Cancel anytime</span>
          </div>
        </div>

        <!-- Product glimpse: today's book -->
        <div class="hero__visual">
          <div class="book sz-navy">
            <div class="sz-pole" />
            <div class="book__head">
              <div>
                <span class="book__eyebrow">Today's book</span>
                <strong class="book__date">{{ todayLabel }}</strong>
              </div>
              <span class="book__fill">6 of 8 filled</span>
            </div>
            <div class="book__bar"><span style="width: 75%" /></div>

            <div v-for="a in sampleBook" :key="a.time" class="appt" :class="'appt--' + a.tone">
              <span class="appt__time">{{ a.time }}</span>
              <span class="appt__rule" />
              <span class="appt__body">
                <strong>{{ a.service }}</strong>
                <small>{{ a.client }} · with {{ a.staff }}</small>
              </span>
              <span class="appt__status">{{ a.status }}</span>
            </div>

            <div class="book__msg">
              <v-icon size="18" color="#C7A66B">mdi-message-text-outline</v-icon>
              <span><b>Jordan:</b> Running 5 min late, still good?</span>
            </div>
          </div>

          <div class="float float--pay">
            <span class="float__icon" style="background: #3f6f55"><v-icon size="18" color="#FFFFFF">mdi-credit-card-check-outline</v-icon></span>
            <span><strong>$86.00 paid</strong><small>Service + $14 tip</small></span>
          </div>
          <div class="float float--alert">
            <span class="float__icon" style="background: #b23a33"><v-icon size="18" color="#FFFFFF">mdi-shield-alert-outline</v-icon></span>
            <span><strong>Care alert</strong><small>Allergic to PPD dye</small></span>
          </div>
        </div>
      </div>

      <div class="sz-wrap">
        <div class="verticals">
          <span class="verticals__label">Built for</span>
          <span v-for="v in verticals" :key="v.label" class="sz-pill">
            <v-icon size="15" color="#A9864A">{{ v.icon }}</v-icon>{{ v.label }}
          </span>
        </div>
      </div>
    </section>

    <!-- ==================== REPLACES STRIP ==================== -->
    <section class="strip">
      <div class="sz-wrap strip__inner">
        <p class="strip__lead serif">One subscription replaces the stack most shops pay for separately.</p>
        <div class="strip__items">
          <div v-for="r in replaces" :key="r.label" class="strip__item">
            <v-icon size="22" color="#C7A66B">{{ r.icon }}</v-icon>
            <span>{{ r.label }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== MODULES ==================== -->
    <section class="sz-section">
      <div class="sz-wrap">
        <div class="sz-head sz-reveal">
          <span class="sz-eyebrow">The whole shop, one app</span>
          <h2 class="sz-h2">Everything from the first booking to the Friday payout.</h2>
          <p class="sz-lede">Every tool your front desk, your team, and your books need, already connected to each other.</p>
        </div>

        <div class="modules">
          <article v-for="(m, i) in modules" :key="m.title" class="sz-card sz-card--hover module sz-reveal" :style="{ transitionDelay: (i % 3) * 70 + 'ms' }">
            <span class="sz-icon" :style="{ background: m.color + '14' }">
              <v-icon size="24" :color="m.color">{{ m.icon }}</v-icon>
            </span>
            <h3>{{ m.title }}</h3>
            <p>{{ m.text }}</p>
            <ul>
              <li v-for="b in m.points" :key="b">{{ b }}</li>
            </ul>
          </article>
        </div>

        <div class="modules__more sz-reveal">
          <router-link to="/features" class="sz-btn sz-btn--ghost">
            Explore all features
            <v-icon size="18" color="#1D2233">mdi-arrow-right</v-icon>
          </router-link>
        </div>
      </div>
    </section>

    <!-- ==================== TRIBZZ ==================== -->
    <section class="tz-band-wrap">
      <div class="sz-wrap">
        <div class="tz-band sz-reveal">
          <div class="tz-band__copy">
            <span class="tz-band__tag">New from our team · Tribzz</span>
            <h2 class="tz-band__title">Give your regulars a place to gather.</h2>
            <p>
              Tribzz is social media for grown-ups, with no ads and no algorithm. Start a Tribzz for your shop so clients can
              see your latest cuts, catch same-day openings and deals, book a chair, and see who's in the shop right now.
            </p>
            <ul class="tz-band__points">
              <li v-for="pt in tribzzPoints" :key="pt"><v-icon size="16" color="#C7A66B">mdi-check-bold</v-icon>{{ pt }}</li>
            </ul>
            <div class="tz-band__ctas">
              <router-link to="/tribzz" class="sz-btn sz-btn--light">
                See how it works
                <v-icon size="18" color="#161C2C">mdi-arrow-right</v-icon>
              </router-link>
              <a :href="tribzzUrl" target="_blank" rel="noopener" class="sz-btn sz-btn--outline-light">Start your Tribzz</a>
            </div>
          </div>
          <div class="tz-band__visual" aria-hidden="true">
            <div class="tz-post">
              <span class="tz-post__kicker">Barber / salon · Public</span>
              <strong>The Corner Chair</strong>
              <span class="tz-post__here"><i />4 here now</span>
              <span class="tz-post__deal">Same-day opening at 3:30. First to book gets it.</span>
              <span class="tz-post__btn">Book a chair</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== A DAY IN THE SHOP ==================== -->
    <section class="sz-section sz-section--paper">
      <div class="sz-wrap">
        <div class="sz-head sz-head--center sz-reveal">
          <span class="sz-eyebrow">How a day runs</span>
          <h2 class="sz-h2">From booked to paid, without the busywork.</h2>
          <p class="sz-lede">Each step hands off to the next, so nothing falls through between the front desk and the chair.</p>
        </div>

        <ol class="flow">
          <li v-for="(s, i) in flow" :key="s.title" class="flow__step sz-reveal" :style="{ transitionDelay: i * 80 + 'ms' }">
            <span class="flow__num">{{ String(i + 1).padStart(2, "0") }}</span>
            <span class="flow__icon"><v-icon size="22" color="#1D2233">{{ s.icon }}</v-icon></span>
            <h3>{{ s.title }}</h3>
            <p>{{ s.text }}</p>
          </li>
        </ol>
      </div>
    </section>

    <!-- ==================== ROLES ==================== -->
    <section class="sz-section">
      <div class="sz-wrap roles">
        <div class="roles__intro sz-reveal">
          <span class="sz-eyebrow">One login, the right view</span>
          <h2 class="sz-h2">Built for everyone who keeps the doors open.</h2>
          <p class="sz-lede">
            Owners see the business. The team sees their book and their earnings. Clients see an
            easy way to book. Everyone gets the app on their phone.
          </p>
          <router-link to="/demo" class="sz-btn sz-btn--primary">See it with your shop</router-link>
        </div>
        <div class="roles__grid">
          <div v-for="r in roles" :key="r.title" class="sz-card role sz-reveal">
            <div class="role__top">
              <span class="sz-icon" :style="{ background: r.color + '14' }"><v-icon size="22" :color="r.color">{{ r.icon }}</v-icon></span>
              <span class="role__badge">{{ r.badge }}</span>
            </div>
            <h3>{{ r.title }}</h3>
            <ul class="sz-checks">
              <li v-for="p in r.points" :key="p">{{ p }}</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== PRICING ==================== -->
    <section id="pricing" class="sz-section sz-section--paper">
      <div class="sz-wrap">
        <div class="sz-head sz-head--center sz-reveal">
          <span class="sz-eyebrow">Simple pricing</span>
          <h2 class="sz-h2">One price. Every feature. Three apps.</h2>
          <p class="sz-lede">No tiers, no per-booking fees, no paying extra for the module you actually need.</p>
        </div>

        <div class="price sz-reveal">
          <div class="price__card sz-navy">
            <div class="sz-pole" />
            <span class="price__badge">Everything included</span>
            <h3>Salon Zap Complete</h3>
            <div class="price__amount">
              <span class="price__cur">$</span>
              <span class="price__num">120</span>
              <span class="price__per">/ month</span>
            </div>
            <p class="price__note">Web app + iPhone + Android · Billed monthly · Cancel anytime</p>
            <router-link to="/demo" class="sz-btn sz-btn--light price__cta">
              Get started
              <v-icon size="18" color="#1D2233">mdi-arrow-right</v-icon>
            </router-link>
            <div class="price__guarantees">
              <span v-for="g in guarantees" :key="g"><v-icon size="16" color="#C7A66B">mdi-check-decagram</v-icon>{{ g }}</span>
            </div>
          </div>

          <div class="price__list">
            <div v-for="g in pricingGroups" :key="g.title" class="price__group">
              <h4><v-icon size="18" color="#B23A33">{{ g.icon }}</v-icon>{{ g.title }}</h4>
              <ul>
                <li v-for="f in g.items" :key="f">{{ f }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== FINAL CTA ==================== -->
    <section class="sz-section">
      <div class="sz-wrap">
        <div class="cta sz-navy sz-reveal">
          <div class="sz-pole" />
          <div>
            <span class="sz-eyebrow sz-eyebrow--light">Ready when you are</span>
            <h2 class="sz-h2">Give your shop the front desk it deserves.</h2>
            <p>We'll set you up, bring over your client list, and train the team, so day one on Salon Zap feels like any other day, just easier.</p>
          </div>
          <div class="cta__actions">
            <router-link to="/demo" class="sz-btn sz-btn--light">Book a free demo</router-link>
            <router-link to="/why" class="sz-btn sz-btn--outline-light">Why Salon Zap</router-link>
          </div>
        </div>
      </div>
    </section>

    <SalonFooter />
  </div>
</template>

<script>
import SalonNavigation from "@/components/SalonNavigation.vue";
import SalonFooter from "@/components/SalonFooter.vue";
import revealOnScroll from "@/mixins/revealOnScroll";
import { TRIBZZ_URL } from "@/utils/tribzz";

export default {
  name: "SalonZapHome",
  components: { SalonNavigation, SalonFooter },
  mixins: [revealOnScroll],
  data() {
    return {
      tribzzUrl: TRIBZZ_URL,
      tribzzPoints: ["Fill openings fast", "Show off your work", "Go live from the chair", "No ads next to your posts"],
      verticals: [
        { label: "Barbershops", icon: "mdi-content-cut" },
        { label: "Hair salons", icon: "mdi-hair-dryer-outline" },
        { label: "Nail studios", icon: "mdi-hand-back-left-outline" },
        { label: "Spas", icon: "mdi-spa-outline" },
        { label: "Lash & brow", icon: "mdi-eye-outline" },
        { label: "Tattoo studios", icon: "mdi-draw" },
        { label: "Wellness", icon: "mdi-leaf" },
        { label: "Mobile pros", icon: "mdi-car-outline" },
      ],
      sampleBook: [
        { time: "10:30", service: "Skin fade & beard trim", client: "Marcus", staff: "Andre", status: "Checked in", tone: "green" },
        { time: "11:15", service: "Gel manicure", client: "Priya", staff: "Lena", status: "Reminder sent", tone: "blue" },
        { time: "1:00", service: "Balayage & blowout", client: "Jordan", staff: "Simone", status: "Deposit paid", tone: "brass" },
      ],
      replaces: [
        { label: "Booking app", icon: "mdi-calendar-month-outline" },
        { label: "Card reader software", icon: "mdi-credit-card-outline" },
        { label: "Client notebook", icon: "mdi-notebook-outline" },
        { label: "Payroll spreadsheet", icon: "mdi-table" },
        { label: "Inventory clipboard", icon: "mdi-clipboard-list-outline" },
        { label: "Group text thread", icon: "mdi-message-text-outline" },
      ],
      modules: [
        {
          icon: "mdi-calendar-month-outline",
          color: "#B23A33",
          title: "Calendar & booking",
          text: "Every chair, room, and appointment on one calendar, filtered by team member.",
          points: ["Set open times per person", "Close the shop for a day in one tap", "Reschedule or cancel without the phone tag"],
        },
        {
          icon: "mdi-web",
          color: "#4E7D63",
          title: "Online booking page",
          text: "Your own booking link for Instagram, Google, and texts, with direct links per service or stylist.",
          points: ["Minimum notice and booking window", "Clients pick who they see, or you assign", "A setup checklist so nothing's missing"],
        },
        {
          icon: "mdi-credit-card-outline",
          color: "#2F4A7A",
          title: "Checkout & payments",
          text: "Ring up services, retail, and tips in one checkout. Take deposits up front.",
          points: ["Deposits applied at checkout", "No-show and late-cancel fees", "Charge the card on file or send a pay link"],
        },
        {
          icon: "mdi-account-heart-outline",
          color: "#7A4E7A",
          title: "Client profiles",
          text: "History, preferences, and formulas so anyone on the team can pick up where the last visit left off.",
          points: ["Allergy and care alerts flagged", "Visits, last visit, and next visit", "Built-in loyalty punch card"],
        },
        {
          icon: "mdi-hand-coin-outline",
          color: "#A9864A",
          title: "Commissions & tips",
          text: "Know exactly what each team member earned this pay period, and what you owe them.",
          points: ["Commission or booth-rent pay models", "Separate retail commission rates", "Record payouts with a full history"],
        },
        {
          icon: "mdi-chart-line",
          color: "#2F7A78",
          title: "Reports & analytics",
          text: "Money collected, average ticket, rebooking rate, and your busiest hours at a glance.",
          points: ["Compare against the period before", "Team performance side by side", "Export any report to CSV"],
        },
        {
          icon: "mdi-message-text-outline",
          color: "#2F7A78",
          title: "Messages & reminders",
          text: "Chat with clients and your team in the app, and send reminders with one tap.",
          points: ["Unread counts and search", "Quick replies for the usual questions", "Appointment reminders from the calendar"],
        },
        {
          icon: "mdi-package-variant-closed",
          color: "#2F4A7A",
          title: "Retail & inventory",
          text: "Track what you sell and the backbar supplies the team goes through.",
          points: ["Low-stock alerts and reorder list", "Margins on every product", "Every restock and sale logged"],
        },
        {
          icon: "mdi-wallet-giftcard",
          color: "#B23A33",
          title: "Gift cards & memberships",
          text: "Sell prepaid packages and monthly memberships that bring clients back on a schedule.",
          points: ["Recurring monthly members", "Gift card balances tracked", "Credits applied right at checkout"],
        },
      ],
      flow: [
        { icon: "mdi-cellphone-check", title: "Booked", text: "A client books from your link at 11pm. The slot is gone for everyone else instantly." },
        { icon: "mdi-bell-ring-outline", title: "Reminded", text: "A reminder goes out before the visit, and replies land in your Messages." },
        { icon: "mdi-card-account-details-star-outline", title: "Recognized", text: "Their profile, care alerts, and last service are ready before they sit down." },
        { icon: "mdi-cash-register", title: "Checked out", text: "Service, retail, and tip in one checkout. Any deposit comes off automatically." },
        { icon: "mdi-bank-transfer-out", title: "Paid out", text: "Commissions and tips add up per team member, ready to pay at the end of the period." },
      ],
      roles: [
        {
          icon: "mdi-store-outline",
          color: "#B23A33",
          badge: "Owners",
          title: "Run the business",
          points: ["Revenue, rebooking, and busiest hours", "Payroll that calculates itself", "Policies, hours, and pricing in one place"],
        },
        {
          icon: "mdi-content-cut",
          color: "#2F4A7A",
          badge: "Your team",
          title: "Work the chair",
          points: ["Their own book on their phone", "Client notes before every visit", "See what they've earned"],
        },
        {
          icon: "mdi-desk",
          color: "#A9864A",
          badge: "Front desk",
          title: "Keep it moving",
          points: ["Seat walk-ins and fill gaps", "Check out in seconds", "Message clients without a personal phone"],
        },
        {
          icon: "mdi-account-heart-outline",
          color: "#3F6F55",
          badge: "Clients",
          title: "Book in a minute",
          points: ["Book anytime from any device", "Reminders before every visit", "Pay a deposit or fee from a link"],
        },
      ],
      guarantees: ["Free setup & training", "We import your client list", "No long-term contract", "30-day money-back guarantee"],
      pricingGroups: [
        { icon: "mdi-calendar-star", title: "Booking", items: ["Unlimited appointments", "Online booking page & links", "Waitlist & walk-ins", "Reminders"] },
        { icon: "mdi-cash-multiple", title: "Money", items: ["Checkout, deposits & tips", "No-show & late-cancel fees", "Commissions & payouts", "Gift cards & memberships"] },
        { icon: "mdi-account-group-outline", title: "Clients & team", items: ["Unlimited client profiles", "Unlimited team accounts", "Messages & quick replies", "Loyalty rewards"] },
        { icon: "mdi-chart-box-outline", title: "Grow", items: ["Reports & CSV export", "Retail & inventory", "Reviews & ratings", "Web, iOS & Android apps"] },
      ],
    };
  },
  computed: {
    todayLabel() {
      return new Date().toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
    },
  },
};
</script>

<style scoped src="../styles/salon-site.css"></style>
<style scoped>
/* ---------- Hero ---------- */
.hero {
  position: relative;
  padding: 140px 0 56px;
  background:
    radial-gradient(50% 60% at 85% 20%, rgba(47, 74, 122, 0.1) 0%, transparent 70%),
    radial-gradient(40% 50% at 10% 90%, rgba(178, 58, 51, 0.07) 0%, transparent 70%),
    var(--ivory);
}

.hero__grid {
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  gap: 56px;
  align-items: center;
}

.hero__title {
  margin: 18px 0 20px;
  font-size: clamp(2.6rem, 5.6vw, 4.2rem);
  line-height: 1.02;
  letter-spacing: -0.035em;
}

.hero__lede {
  max-width: 520px;
}

.hero__ctas {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 30px;
}

.hero__trust {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  margin-top: 22px;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--ink-soft);
}

.hero__trust span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

/* Today's book mock */
.hero__visual {
  position: relative;
  padding: 24px 0 32px;
}

.book {
  border-radius: 26px;
  padding: 26px 24px 22px 34px;
  box-shadow: 0 40px 70px -40px rgba(22, 28, 44, 0.7);
  animation: rise 0.7s ease both;
}

.book__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.book__eyebrow {
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--brass);
}

.book__date {
  font-family: "Fraunces", Georgia, serif;
  font-weight: 500;
  font-size: 22px;
}

.book__fill {
  font-size: 12.5px;
  color: rgba(246, 241, 232, 0.6);
}

.book__bar {
  height: 4px;
  border-radius: 4px;
  background: rgba(246, 241, 232, 0.1);
  margin: 14px 0 18px;
  overflow: hidden;
}

.book__bar span {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, var(--brass), #e7d7b8);
}

.appt {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 16px;
  background: rgba(246, 241, 232, 0.06);
  border: 1px solid rgba(246, 241, 232, 0.07);
  margin-bottom: 10px;
}

.appt__time {
  width: 44px;
  font-weight: 700;
  font-size: 15px;
}

.appt__rule {
  width: 3px;
  align-self: stretch;
  border-radius: 3px;
  background: var(--brass);
}

.appt--green .appt__rule { background: #6fd39a; }
.appt--blue .appt__rule { background: #7f9bd1; }

.appt__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.appt__body strong {
  font-size: 14.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.appt__body small {
  font-size: 12.5px;
  color: rgba(246, 241, 232, 0.55);
}

.appt__status {
  flex: 0 0 auto;
  font-size: 11.5px;
  font-weight: 700;
  padding: 5px 10px;
  border-radius: 999px;
  background: rgba(246, 241, 232, 0.08);
  color: rgba(246, 241, 232, 0.8);
}

.appt--green .appt__status {
  background: rgba(111, 211, 154, 0.14);
  color: #8fe0b0;
}

.book__msg {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px dashed rgba(246, 241, 232, 0.15);
  font-size: 13.5px;
  color: rgba(246, 241, 232, 0.75);
}

.book__msg b {
  color: var(--cream);
}

.float {
  position: absolute;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px 10px 10px;
  border-radius: 16px;
  background: var(--paper);
  border: 1px solid var(--line);
  box-shadow: 0 20px 40px -24px rgba(22, 28, 44, 0.5);
  animation: bob 6s ease-in-out infinite;
}

.float span:last-child {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
}

.float strong {
  font-size: 13.5px;
}

.float small {
  font-size: 12px;
  color: var(--muted);
}

.float__icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.float--pay {
  right: -18px;
  bottom: -14px;
}

.float--alert {
  left: -26px;
  top: -10px;
  animation-delay: -3s;
}

.verticals {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 56px;
  padding-top: 28px;
  border-top: 1px solid var(--line);
}

.verticals__label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--muted);
  margin-right: 6px;
}

/* ---------- Strip ---------- */
.strip {
  background: var(--navy);
  color: var(--cream);
  padding: 36px 0;
}

.strip__inner {
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 40px;
  align-items: center;
}

.strip__lead {
  margin: 0;
  font-size: 1.35rem;
  line-height: 1.3;
}

.strip__items {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 14px;
}

.strip__item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: rgba(246, 241, 232, 0.7);
  text-decoration: line-through;
  text-decoration-color: rgba(178, 58, 51, 0.8);
  text-decoration-thickness: 2px;
}

/* ---------- Modules ---------- */
.modules {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.module h3 {
  font-size: 1.35rem;
  margin: 18px 0 8px;
}

.module p {
  margin: 0 0 16px;
  font-size: 14.5px;
  line-height: 1.6;
  color: var(--ink-soft);
}

.module ul {
  list-style: none;
  padding: 14px 0 0;
  margin: 0;
  border-top: 1px dashed var(--line);
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.module li {
  position: relative;
  padding-left: 16px;
  font-size: 13.5px;
  color: var(--ink-soft);
}

.module li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 8px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--brass);
}

.modules__more {
  display: flex;
  justify-content: center;
  margin-top: 36px;
}

/* ---------- Flow ---------- */
.flow {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 14px;
  position: relative;
}

.flow::before {
  content: "";
  position: absolute;
  top: 46px;
  left: 8%;
  right: 8%;
  height: 2px;
  background: repeating-linear-gradient(90deg, var(--line-strong) 0 8px, transparent 8px 14px);
}

.flow__step {
  position: relative;
  text-align: center;
  padding: 0 6px;
}

.flow__num {
  display: block;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.14em;
  color: var(--red);
  margin-bottom: 10px;
}

.flow__icon {
  position: relative;
  z-index: 1;
  width: 56px;
  height: 56px;
  margin: 0 auto 16px;
  border-radius: 50%;
  background: var(--ivory);
  border: 1px solid var(--line-strong);
  display: flex;
  align-items: center;
  justify-content: center;
}

.flow__step h3 {
  font-size: 1.2rem;
  margin-bottom: 8px;
}

.flow__step p {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: var(--ink-soft);
}

/* ---------- Roles ---------- */
.roles {
  display: grid;
  grid-template-columns: 0.9fr 1.3fr;
  gap: 48px;
  align-items: start;
}

.roles__intro {
  position: sticky;
  top: 110px;
}

.roles__intro .sz-h2 {
  margin: 14px 0 16px;
}

.roles__intro .sz-btn {
  margin-top: 26px;
}

.roles__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.role__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.role__badge {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--sand);
  color: #8a6c36;
}

.role h3 {
  font-size: 1.3rem;
  margin: 18px 0 14px;
}

/* ---------- Pricing ---------- */
.price {
  display: grid;
  grid-template-columns: 380px 1fr;
  gap: 24px;
  align-items: stretch;
}

.price__card {
  border-radius: 26px;
  padding: 34px 30px 30px 40px;
  display: flex;
  flex-direction: column;
}

.price__badge {
  align-self: flex-start;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 5px 12px;
  border-radius: 999px;
  background: rgba(199, 166, 107, 0.16);
  color: var(--brass);
}

.price__card h3 {
  font-size: 1.6rem;
  margin: 16px 0 8px;
}

.price__amount {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin: 6px 0 8px;
}

.price__cur {
  font-family: "Fraunces", Georgia, serif;
  font-size: 2rem;
  margin-top: 10px;
  color: var(--brass);
}

.price__num {
  font-family: "Fraunces", Georgia, serif;
  font-size: 5.2rem;
  font-weight: 500;
  line-height: 1;
  letter-spacing: -0.04em;
}

.price__per {
  align-self: flex-end;
  margin-bottom: 12px;
  font-size: 15px;
  color: rgba(246, 241, 232, 0.65);
}

.price__note {
  margin: 0 0 22px;
  font-size: 13.5px;
  color: rgba(246, 241, 232, 0.6);
}

.price__cta {
  width: 100%;
}

.price__guarantees {
  display: flex;
  flex-direction: column;
  gap: 9px;
  margin-top: 22px;
  padding-top: 20px;
  border-top: 1px solid rgba(246, 241, 232, 0.12);
  font-size: 13.5px;
  color: rgba(246, 241, 232, 0.8);
}

.price__guarantees span {
  display: flex;
  align-items: center;
  gap: 8px;
}

.price__list {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px;
  background: var(--line);
  border: 1px solid var(--line);
  border-radius: 26px;
  overflow: hidden;
}

.price__group {
  background: var(--paper);
  padding: 26px 26px 24px;
}

.price__group h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 14px;
  font-family: "Fraunces", Georgia, serif;
  font-weight: 500;
  font-size: 1.2rem;
}

.price__group ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.price__group li {
  font-size: 14px;
  color: var(--ink-soft);
  padding-left: 22px;
  position: relative;
}

.price__group li::before {
  content: "✓";
  position: absolute;
  left: 0;
  color: var(--green);
  font-weight: 700;
}

/* ---------- CTA ---------- */
.cta {
  border-radius: 30px;
  padding: 56px 56px 56px 64px;
  display: grid;
  grid-template-columns: 1.4fr auto;
  gap: 40px;
  align-items: center;
}

.cta .sz-h2 {
  margin: 14px 0 12px;
  color: var(--cream);
}

.cta p {
  margin: 0;
  max-width: 560px;
  font-size: 15.5px;
  line-height: 1.7;
  color: rgba(246, 241, 232, 0.7);
}

.cta__actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

@keyframes rise {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: none; }
}

@keyframes bob {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}

@media (prefers-reduced-motion: reduce) {
  .book, .float { animation: none; }
}

/* ---------- Responsive ---------- */
@media (max-width: 1080px) {
  .strip__inner { grid-template-columns: 1fr; gap: 20px; }
  .strip__items { grid-template-columns: repeat(3, 1fr); }
  .modules { grid-template-columns: 1fr 1fr; }
  .flow { grid-template-columns: repeat(3, 1fr); row-gap: 36px; }
  .flow::before { display: none; }
  .roles { grid-template-columns: 1fr; }
  .roles__intro { position: static; }
  .price { grid-template-columns: 1fr; }
}

@media (max-width: 900px) {
  .hero { padding-top: 112px; }
  .hero__grid { grid-template-columns: 1fr; gap: 32px; }
  .float--pay { right: 0; }
  .float--alert { left: 0; }
  .cta { grid-template-columns: 1fr; padding: 40px 28px 40px 36px; }
  .cta__actions { flex-direction: row; flex-wrap: wrap; }
}

@media (max-width: 640px) {
  .modules,
  .roles__grid,
  .price__list { grid-template-columns: 1fr; }
  .flow { grid-template-columns: 1fr 1fr; }
  .strip__items { grid-template-columns: 1fr 1fr; }
  .book { padding: 22px 16px 18px 26px; }
  .appt__status { display: none; }
  .hero__ctas .sz-btn { flex: 1; }
  .price__card { padding: 30px 22px 24px 32px; }
}
/* ---------- Tribzz band ---------- */
.tz-band-wrap {
  padding: 0 0 96px;
}
.tz-band {
  display: grid;
  grid-template-columns: 1.3fr 1fr;
  gap: 40px;
  align-items: center;
  padding: 48px;
  border-radius: 28px;
  background: radial-gradient(70% 120% at 100% 0%, rgba(178, 58, 51, 0.35) 0%, transparent 60%), #1b1715;
  color: #f3ede3;
}
.tz-band__tag {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--brass);
}
.tz-band__title {
  margin: 12px 0 14px;
  font-size: clamp(1.9rem, 3.6vw, 2.6rem);
  line-height: 1.1;
  color: #fff;
}
.tz-band__copy p {
  margin: 0;
  max-width: 560px;
  line-height: 1.7;
  color: rgba(243, 237, 227, 0.78);
}
.tz-band__points {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 18px;
  margin: 18px 0 0;
  padding: 0;
  list-style: none;
  font-size: 14px;
  font-weight: 600;
}
.tz-band__points li {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.tz-band__ctas {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 26px;
}
.tz-band__visual {
  display: flex;
  justify-content: center;
}
.tz-post {
  width: 100%;
  max-width: 320px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 22px;
  background: #f3ede3;
  color: #1b1715;
  font-family: Georgia, "Times New Roman", serif;
  border-top: 4px solid #a8352d;
  box-shadow: 0 30px 60px -30px rgba(0, 0, 0, 0.6);
  transform: rotate(-1.5deg);
}
.tz-post__kicker {
  font-family: "DM Sans", sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #a8352d;
}
.tz-post strong {
  font-size: 22px;
}
.tz-post__here {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-style: italic;
  font-size: 14px;
  color: #3d342e;
}
.tz-post__here i {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #2d7a4f;
  box-shadow: 0 0 0 3px rgba(45, 122, 79, 0.25);
}
.tz-post__deal {
  padding: 10px 12px;
  border-left: 3px solid #c7a66b;
  background: #ebe3d6;
  font-style: italic;
  font-size: 14px;
}
.tz-post__btn {
  margin-top: 4px;
  padding: 10px 0;
  background: #1b1715;
  color: #f3ede3;
  text-align: center;
  font-family: "DM Sans", sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}
@media (max-width: 900px) {
  .tz-band {
    grid-template-columns: 1fr;
    padding: 34px 24px;
  }
  .tz-band-wrap {
    padding-bottom: 64px;
  }
}
</style>